import loadable from "@loadable/component"
import { AnimatePresence, motion } from "framer-motion"
import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigationType } from "react-router-dom"
import ScrollLock, {
  TouchScrollable,
} from "react-scrolllock-with-horizontal-scroll"

import { toggleCartPreview } from "areas/cart/store/actions"
import { CloseButton } from "components/ui/modal"
import { retry } from "helpers/application"

const PopupContent = loadable(() => retry(() => import("../popup/content")))
const Cta = loadable(() => retry(() => import("../popup/cta")))

const variants = {
  hide: {
    right: "-100%",
    transition: { duration: 0.5 },
  },
  show: {
    right: 0,
    transition: { duration: 0.5 },
  },
}

const CartPreviewModal = () => {
  const [sticky, setSticky] = useState(false)
  const ctaRef = useRef()
  const contentRef = useRef()
  const action = useNavigationType()
  const dispatch = useDispatch()
  const lineItems = useSelector((state) => state.cart.line_items)
  const showSidebar = useSelector((state) => state.cart.showSidebar)

  const closeCartPreview = useCallback(() => {
    dispatch(toggleCartPreview())
  }, [dispatch])

  useEffect(() => {
    if (action !== "REPLACE" && showSidebar) {
      closeCartPreview()
    }
  }, [action])

  useEffect(() => {
    if (!showSidebar) {
      setSticky(false)
    }
  }, [showSidebar])

  useEffect(() => {
    if (contentRef && contentRef.current) {
      const content = contentRef.current
      const checkScrollPosition = () => {
        if (ctaRef && ctaRef.current) {
          const cta = ctaRef.current
          const rect = cta.getBoundingClientRect()
          const offsetTop = content.offsetTop
          if (rect) {
            setSticky(offsetTop > rect.top)
          }
        }
      }
      content.addEventListener("scroll", checkScrollPosition)
      return () => content.removeEventListener("scroll", checkScrollPosition)
    }
  })

  return (
    <>
      <AnimatePresence>
        {showSidebar && (
          <motion.div
            animate="show"
            className="no-scrollbar max-w-375 fixed bottom-0 right-0 z-50 h-full w-11/12 overflow-y-auto bg-white"
            exit="hide"
            initial="hide"
            transition={{ type: "linear" }}
            variants={variants}
          >
            <ScrollLock isActive>
              <div
                className="relative flex h-full max-h-none w-full flex-col overflow-y-hidden bg-white pt-12 transition-all duration-200"
                data-cy="cart-atc-modal"
              >
                <div className="mx-16 mb-12 flex h-32 flex-auto items-center justify-end">
                  <CloseButton handleClose={closeCartPreview} />
                </div>
                {sticky && (
                  <div className="absolute top-32 z-20 w-full bg-white px-16 pb-24 shadow-md">
                    <Cta />
                  </div>
                )}
                <TouchScrollable>
                  <div
                    className="tablet:h-full tablet:overflow-y-hidden tablet:pb-32 flex flex-auto flex-col overflow-y-auto overflow-x-hidden px-16"
                    ref={contentRef}
                  >
                    {lineItems?.length > 0 && (
                      <PopupContent ctaRef={ctaRef} sticky={sticky} />
                    )}
                  </div>
                </TouchScrollable>
              </div>
            </ScrollLock>
          </motion.div>
        )}
      </AnimatePresence>
      {showSidebar && (
        <div
          className="fixed inset-0 z-40 bg-[rgba(0,0,0,0.1)]"
          onClick={closeCartPreview}
        />
      )}
    </>
  )
}

export default CartPreviewModal
